// SPDX-License-Identifier: Apache-2.0
import GML3 from 'ol/format/GML3';
import GML32 from 'ol/format/GML32';
import { Options as GmlOptions } from 'ol/format/GMLBase';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import { makeReplacer } from 'ol/xml';

// OpenLayers 10.8.0 does not register gml:CompositeCurve as a root geometry,
// so WFS parsing can treat it as a complex feature attribute and later crash
// when that attribute is used as an OpenLayers geometry. Keep the workaround
// local to GeoGirafe's WFS/GML parsing instead of patching OpenLayers itself.

// NOTE REG: This file was fully generated with codex/superpowers
// It solved a very specific case that is not supported by OpenLayers for now
// In the future (when OL will be supporting `gml:CompositeCurve`)
// this workaround can be removed.

type LinePiece = number[][];
type CompositeGeometry = LineString | MultiLineString;
type ObjectStack = Array<unknown>;
type CompositeCurveParser = GML3 & {
  readCompositeCurve(node: Node, objectStack: ObjectStack): CompositeGeometry;
  readOrientableCurve(node: Node, objectStack: ObjectStack): CompositeGeometry;
};

const GML31_NS = 'http://www.opengis.net/gml';
const GML32_NS = 'http://www.opengis.net/gml/3.2';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

export class CompositeCurveGML3 extends GML3 {
  public constructor(options?: GmlOptions) {
    super(options);
    registerCompositeCurveParser(this, GML31_NS);
  }

  public readCompositeCurve(node: Node, objectStack: ObjectStack): CompositeGeometry {
    return readCompositeCurve(this, node as Element, objectStack);
  }

  public readOrientableCurve(node: Node, objectStack: ObjectStack): CompositeGeometry {
    return readOrientableCurve(this, node as Element, objectStack);
  }
}

export class CompositeCurveGML32 extends GML32 {
  public constructor(options?: GmlOptions) {
    super(options);
    registerCompositeCurveParser(this, GML32_NS);
  }

  public readCompositeCurve(node: Node, objectStack: ObjectStack): CompositeGeometry {
    return readCompositeCurve(this, node as Element, objectStack);
  }

  public readOrientableCurve(node: Node, objectStack: ObjectStack): CompositeGeometry {
    return readOrientableCurve(this, node as Element, objectStack);
  }
}

function registerCompositeCurveParser(parser: CompositeCurveParser, namespace: string): void {
  parser.GEOMETRY_PARSERS[namespace] = {
    ...parser.GEOMETRY_PARSERS[namespace],
    CompositeCurve: makeReplacer(parser.readCompositeCurve, parser)
  };
  parser.CURVEMEMBER_PARSERS[namespace] = {
    ...parser.CURVEMEMBER_PARSERS[namespace],
    CompositeCurve: makeReplacer(parser.readCompositeCurve, parser),
    OrientableCurve: makeReplacer(parser.readOrientableCurve, parser)
  };
}

function readCompositeCurve(parser: CompositeCurveParser, node: Element, objectStack: ObjectStack): CompositeGeometry {
  const pieces: LinePiece[] = [];

  for (const member of Array.from(node.children)) {
    if (member.localName !== 'curveMember' && member.localName !== 'curveMembers') {
      continue;
    }
    pieces.push(...readCompositeCurveMember(parser, member, objectStack));
  }

  if (pieces.length === 0) {
    console.warn(`CompositeCurve at ${describeNode(node)} did not contain convertible curve members.`);
    return new MultiLineString([]);
  }

  return buildCompositeGeometry(pieces);
}

function readCompositeCurveMember(
  parser: CompositeCurveParser,
  member: Element,
  objectStack: ObjectStack
): LinePiece[] {
  const href = member.getAttributeNS(XLINK_NS, 'href') ?? member.getAttribute('xlink:href');
  if (href) {
    console.warn(`CompositeCurve member reference ${href} cannot be resolved locally.`);
    return [];
  }

  const pieces: LinePiece[] = [];
  for (const child of Array.from(member.children)) {
    pieces.push(...readCurveElement(parser, child, objectStack));
  }

  if (pieces.length === 0) {
    console.warn(`CompositeCurve member at ${describeNode(member)} did not contain a convertible curve.`);
  }
  return pieces;
}

function readCurveElement(parser: CompositeCurveParser, node: Element, objectStack: ObjectStack): LinePiece[] {
  if (node.localName === 'LineString') {
    return lineStringToPieces(parser.readLineString(node, objectStack));
  }
  if (node.localName === 'Curve') {
    warnIfCurveHasUnsupportedSegments(node);
    return lineStringToPieces(parser.readCurve(node, objectStack));
  }
  if (node.localName === 'CompositeCurve') {
    return geometryToPieces(parser.readCompositeCurve(node, objectStack));
  }
  if (node.localName === 'OrientableCurve') {
    return geometryToPieces(parser.readOrientableCurve(node, objectStack));
  }

  console.warn(`CompositeCurve member contains unsupported ${describeNode(node)}.`);
  return [];
}

function readOrientableCurve(parser: CompositeCurveParser, node: Element, objectStack: ObjectStack): CompositeGeometry {
  const baseCurve = Array.from(node.children).find((child) => child.localName === 'baseCurve');
  if (!baseCurve) {
    console.warn(`OrientableCurve at ${describeNode(node)} does not contain baseCurve.`);
    return new MultiLineString([]);
  }

  const pieces = Array.from(baseCurve.children).flatMap((child) => readCurveElement(parser, child, objectStack));
  if (node.getAttribute('orientation') === '-') {
    pieces.reverse();
    for (const piece of pieces) {
      piece.reverse();
    }
  }

  return buildCompositeGeometry(pieces);
}

function warnIfCurveHasUnsupportedSegments(node: Element): void {
  const segments = Array.from(node.children).find((child) => child.localName === 'segments');
  if (!segments) {
    return;
  }
  const unsupportedSegment = Array.from(segments.children).find((child) => child.localName !== 'LineStringSegment');
  if (unsupportedSegment) {
    console.warn(`CompositeCurve member contains unsupported ${describeNode(unsupportedSegment)} segment.`);
  }
}

function lineStringToPieces(lineString: LineString | undefined): LinePiece[] {
  if (!lineString) {
    return [];
  }
  const coordinates = lineString.getCoordinates();
  return coordinates.length > 0 ? [coordinates] : [];
}

function geometryToPieces(geometry: CompositeGeometry): LinePiece[] {
  if (geometry instanceof LineString) {
    return lineStringToPieces(geometry);
  }
  return geometry.getCoordinates();
}

function buildCompositeGeometry(pieces: LinePiece[]): CompositeGeometry {
  const normalizedPieces = pieces.filter((piece) => piece.length > 0);
  if (normalizedPieces.length === 0) {
    return new MultiLineString([]);
  }

  const groups: LinePiece[] = [];
  for (const piece of normalizedPieces) {
    const current = clonePiece(piece);
    const previous = groups.at(-1);
    if (previous && coordinatesEqual(previous[previous.length - 1], current[0])) {
      previous.push(...current.slice(1));
    } else {
      groups.push(current);
    }
  }

  return groups.length === 1 ? new LineString(groups[0]) : new MultiLineString(groups);
}

function clonePiece(piece: LinePiece): LinePiece {
  return piece.map((coordinate) => [...coordinate]);
}

function coordinatesEqual(first: number[], second: number[]): boolean {
  if (first.length !== second.length) {
    console.warn('CompositeCurve contains members with inconsistent coordinate dimensions.');
    return false;
  }
  return first.every((value, index) => value === second[index]);
}

function describeNode(node: Element): string {
  const id = node.getAttribute('gml:id') ?? node.getAttribute('id');
  return id ? `${node.localName}#${id}` : node.localName;
}
